import React, {useCallback, useEffect, useState} from 'react';
import {CFComponentProps} from 'module/CustomField/type/CFComponentType';
import {CFValue} from 'module/CustomField/type/CFType';
import {useDqSearchRx} from 'module/CustomField/component/input/hook/useDqSearchRx'
import {getReactSelectDefaultProps} from 'config/react-select'
import {DqElement, isDqSearchable} from 'module/Dq/searchDq'
import {log} from 'common/log'
import {delay, filter, take, tap} from 'rxjs/operators'
import {SortableMultiSelect, SortableMultiSelectComponents, SortableMultiSelectProps} from 'component/SortableMultiSelect'
import {arrayMove} from 'react-sortable-hoc'
import {filterOption, filterOptionReactSelect} from 'module/CustomField/component/input/ReactSelectComponent/FilterOption'
import {arrayToStringValue, useMultiValue} from 'module/CustomField/component/util/MultiInputUtil'
import Select from 'react-select'
import {GroupBase} from 'react-select/dist/declarations/src/types'
import {CustomFieldSelectOption} from 'module/CustomField/component/input/ReactSelectComponent/CustomFieldSelectOption'
import {useMemoDq} from 'module/Memo/hook/useMemoDq'

const customComponents: SortableMultiSelectComponents<DqElement> = {
	DropdownIndicator: undefined, // click on dropdown indicator results in invisible value!
	Option: CustomFieldSelectOption,
}

export interface CustomFieldSelectMultiComponentCustomProps {
	dispatchMemoToggleAction?: (x?: DqElement) => void
}

export function CustomFieldSelectMultiComponent(props: CFComponentProps<CFValue>) {
	const {
		cfDef,
		setCb,
		model,
		modelValue,
		disabled,
		placeholderHTML,
	} = props

	const {
		searchSubject,
		suggestions: _suggestions,
		labelCache,
		loading,
		responseSubject,
	} = useDqSearchRx(props)

	const [inputValue, setInputValue] = useState('')
	const [disableManualSortPossible, setDisableManualSortPossible] = useState(false)
	let {valueList, onChange, multiInputParameters: {separator, maxAmount, disableManualSort}} = useMultiValue(cfDef, modelValue, setCb, disabled)
	const {memoElements, dispatchMemoAutoAddAction, dispatchMemoToggleAction} = useMemoDq({cfDef, modelValue, inputValue})

	let suggestions = memoElements.length ? _suggestions.filter(x => !memoElements.some(m => m.value == x.value)) : _suggestions
	suggestions = [...memoElements, ...suggestions]

	const _onChange = onChange
	onChange = (values, action) => {
		log.debug(values, action)
		const selected = values[values.length - 1]
		if (selected && action.action === 'select-option') dispatchMemoAutoAddAction(selected)
		return _onChange(values, action)
	}

	if (!disableManualSortPossible) {
		if (disableManualSort) log.warn('disableManualSort was not possible because of searchable dq', cfDef)
		disableManualSort = false
	}
	if (disableManualSort) {
		const sugVals = suggestions.map(x => x.value)
		valueList = [...valueList].sort((a, b) => sugVals.indexOf(a) - sugVals.indexOf(b))
	}
	const mappedValueList = valueList.map(x => suggestions.find(s => s.value === x) || ({value: x, label: labelCache[x]}))
	// show suggestions in order: 1.selected values, 2. unselected favorites, 3. other values

	useEffect(() => searchSubject.next({searchTerm: inputValue, model}), [inputValue, model])

	useEffect(() => {
		// initializes missing labels by search yb value (only once)
		const sub = responseSubject.pipe(
			filter(x => !!x),
			take(1), // after first result
			delay(200),
			tap((res) => {
				if (res?.searchable) {
					setDisableManualSortPossible(false)
					const {labelCache} = res
					valueList.forEach(searchTerm => {
						if (!labelCache[searchTerm]) {
							searchSubject.next({searchTerm, model, onlyLabelCache: true})
						}
					})
				} else {
					setDisableManualSortPossible(true)
				}
			}),
		).subscribe()
		return () => sub.unsubscribe()
	}, [searchSubject, responseSubject, cfDef, model])

	const onInputChange = useCallback((value, {action}) => {
		switch (action) {
			case 'input-change':
				setInputValue(value);
				break;
			case 'set-value':
			case 'input-blur':
			case 'menu-close':
				setInputValue('');
				break;
			default:
				log.error('noop', action);
		}
	}, [])

	const onSortEnd = useCallback(({oldIndex, newIndex}) => {
		setCb(arrayToStringValue(arrayMove(valueList, oldIndex, newIndex), separator, maxAmount))
	}, [valueList, separator, setCb, maxAmount])

	const SelectImpl = disableManualSort ? Select : SortableMultiSelect
	return <div className={'CustomFieldSelectMultiComponent'}>
		<SelectImpl
			{...getReactSelectDefaultProps<DqElement, true, GroupBase<DqElement>, SortableMultiSelectProps<DqElement> & CustomFieldSelectMultiComponentCustomProps>({
				components: customComponents,
				placeholder: placeholderHTML && <div dangerouslySetInnerHTML={{__html: placeholderHTML}}/>,
				isDisabled: disabled,
				isMulti: true,
				isClearable: true,
				tabSelectsValue: true,
				closeMenuOnSelect: false,
				options: suggestions,
				isLoading: loading,
				value: mappedValueList,
				onChange,
				onSortEnd,
				inputValue: inputValue,
				onInputChange: onInputChange,
				filterOption: isDqSearchable(cfDef.dqKey) ? null : filterOptionReactSelect,
				dispatchMemoToggleAction,
			})}
		/>
	</div>
}